import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Building2, BookOpen, Search } from "lucide-react";

let catalogCache = null;
const loadCatalog = async () => {
  if (!catalogCache) {
    const [s, p] = await Promise.all([api.get("/catalog/schools"), api.get("/catalog/programs")]);
    catalogCache = [...s.data, ...p.data];
  }
  return catalogCache;
};

export function TagDialog({ conv, onClose, onSave }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);

  useEffect(() => {
    if (!conv) return;
    setSel((conv.tags || []).map((t) => t.external_id));
    setQ("");
    loadCatalog().then(setItems).catch(() => setItems([]));
  }, [conv]);

  const ql = q.trim().toLowerCase();
  const shown = items.filter((i) => !ql || [i.title, i.subtitle].some((v) => v && v.toLowerCase().includes(ql)));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 10 ? s : [...s, id]));

  return (
    <Dialog open={!!conv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="conv-tag-dialog">
        <DialogHeader>
          <DialogTitle className="text-navy">Tag this conversation</DialogTitle>
          <DialogDescription>Link it to schools or programs to find it again easily (up to 10).</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input data-testid="conv-tag-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search schools or programs…" className="h-10 pl-8 rounded-xl" />
        </div>
        <div className="max-h-72 overflow-y-auto -mx-1 space-y-0.5">
          {shown.map((i) => {
            const Icon = i.item_type === "program" ? BookOpen : Building2;
            return (
              <label key={i.external_id} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-secondary cursor-pointer">
                <Checkbox data-testid={`conv-tag-option-${i.external_id}`} checked={sel.includes(i.external_id)} onCheckedChange={() => toggle(i.external_id)} />
                <Icon className="h-4 w-4 text-orange shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm text-navy truncate">{i.title}</span>
                  {i.subtitle && <span className="block text-[11px] text-muted-foreground truncate">{i.subtitle}</span>}
                </span>
              </label>
            );
          })}
          {shown.length === 0 && <p className="text-sm text-muted-foreground px-2 py-3">No match.</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="conv-tag-cancel">Cancel</Button>
          <Button className="rounded-full bg-orange hover:bg-orange-dark text-white" onClick={() => onSave(sel)} data-testid="conv-tag-save">Save tags</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
